import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import {
  COUNTRY_ISD_CODES,
  findCountryByDialCode,
  normalizeDialCodeForCompare,
} from "../data/countryIsdCodes";

const LIST_MAX_HEIGHT = 280;

/**
 * Searchable dropdown for Country ISD codes.
 * value: dial_code string (e.g. "+91")
 * onChange: (dial_code) => void
 */
export function SearchableISDSelect({
  value,
  onChange,
  disabled,
  placeholder = "Search or select country code",
  className,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  const selected = findCountryByDialCode(value);
  const displayText = selected
    ? `${selected.name} (${selected.dial_code})`
    : "";

  const valueNorm = normalizeDialCodeForCompare(value);

  const filtered = search.trim()
    ? COUNTRY_ISD_CODES.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.dial_code.includes(search.replace(/\s/g, "")),
      )
    : COUNTRY_ISD_CODES;

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open]);

  const handleSelect = (item) => {
    onChange(item.dial_code);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter" && filtered.length === 1) {
      e.preventDefault();
      handleSelect(filtered[0]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-left ring-offset-background",
            "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !displayText && "text-muted-foreground",
            className,
          )}
        >
          <span className="truncate">{displayText || placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-2 border-b">
          <Input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search country or code..."
            className="h-9"
          />
        </div>
        <div
          ref={listRef}
          className="overflow-y-auto overscroll-contain py-1"
          style={{ maxHeight: LIST_MAX_HEIGHT }}
        >
          {filtered.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No matches
            </div>
          ) : (
            filtered.map((item) => (
              <button
                key={item.code}
                type="button"
                className={cn(
                  "flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-sm",
                  "hover:bg-accent hover:text-accent-foreground",
                  valueNorm === normalizeDialCodeForCompare(item.dial_code) &&
                    "bg-accent text-accent-foreground",
                )}
                onClick={() => handleSelect(item)}
              >
                <span className="truncate">{item.name}</span>
                <span className="shrink-0 font-medium">{item.dial_code}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableISDSelect;
